import type { Integration, IntegrationType } from '@yawi/types';
import {
  Mail,
  Smartphone,
  MessageSquare,
  Bell,
  CreditCard,
  Camera,
  Cloud,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Play,
  Pause,
  Trash2,
  Edit2,
  Zap,
  Loader2,
} from 'lucide-react';
import { Card, Button } from '@/core/ui/components';
import { useConfirmation } from '@/app/providers';

interface IntegrationListProps {
  integrations: Integration[];
  isLoading: boolean;
  isTesting: boolean;
  onEdit: (integration: Integration) => void;
  onDelete: (id: string) => void;
  onTest: (id: string) => void;
  onEnable: (id: string) => void;
  onDisable: (id: string) => void;
}

const typeIcons: Record<IntegrationType, React.ReactNode> = {
  EMAIL: <Mail className="w-5 h-5" />,
  SMS: <Smartphone className="w-5 h-5" />,
  WHATSAPP: <MessageSquare className="w-5 h-5" />,
  PUSH: <Bell className="w-5 h-5" />,
  PAYMENT: <CreditCard className="w-5 h-5" />,
  ACCESS: <Camera className="w-5 h-5" />,
  STORAGE: <Cloud className="w-5 h-5" />,
};

const typeLabels: Record<IntegrationType, string> = {
  EMAIL: 'Email',
  SMS: 'SMS',
  WHATSAPP: 'WhatsApp',
  PUSH: 'Push Notifications',
  PAYMENT: 'Pagos',
  ACCESS: 'Control de Acceso',
  STORAGE: 'Almacenamiento',
};

const typeDescriptions: Record<IntegrationType, string> = {
  EMAIL: 'Correo electrónico transaccional y marketing',
  SMS: 'Mensajes de texto',
  WHATSAPP: 'Mensajes de WhatsApp',
  PUSH: 'Notificaciones push',
  PAYMENT: 'Procesamiento de pagos',
  ACCESS: 'Control de acceso y videovigilancia',
  STORAGE: 'Almacenamiento de archivos en la nube',
};

const statusColors = {
  ENABLED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  DISABLED: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  ERROR: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const statusIcons = {
  ENABLED: <CheckCircle className="w-3 h-3" />,
  DISABLED: <Pause className="w-3 h-3" />,
  ERROR: <AlertTriangle className="w-3 h-3" />,
};

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Nunca';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Hace un momento';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return date.toLocaleDateString();
}

export function IntegrationList({
  integrations,
  isLoading,
  isTesting,
  onEdit,
  onDelete,
  onTest,
  onEnable,
  onDisable,
}: IntegrationListProps) {
  const { confirm } = useConfirmation();

  // Group integrations by type
  const groupedIntegrations = integrations.reduce((acc, integration) => {
    if (!acc[integration.type]) {
      acc[integration.type] = [];
    }
    acc[integration.type].push(integration);
    return acc;
  }, {} as Record<IntegrationType, Integration[]>);

  // Sort each group by priority
  Object.values(groupedIntegrations).forEach(group => {
    group.sort((a, b) => a.priority - b.priority);
  });

  const handleDelete = async (integration: Integration) => {
    const confirmed = await confirm({
      title: 'Eliminar Integración',
      message: `¿Estás seguro de eliminar "${integration.name}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (confirmed) {
      onDelete(integration.id);
    }
  };

  const handleToggleStatus = (integration: Integration) => {
    if (integration.status === 'ENABLED') {
      onDisable(integration.id);
    } else {
      onEnable(integration.id);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 animate-spin text-brand-500" />
        </div>
      </Card>
    );
  }

  const types: IntegrationType[] = ['EMAIL', 'SMS', 'WHATSAPP', 'PUSH', 'PAYMENT', 'ACCESS', 'STORAGE'];

  return (
    <div className="space-y-6">
      {types.map((type) => {
        const typeIntegrations = groupedIntegrations[type] || [];

        return (
          <div key={type}>
            {/* Type Header */}
            <div className="flex items-center gap-3 mb-3">
              <span className="text-gray-500 dark:text-gray-400">
                {typeIcons[type]}
              </span>
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90 uppercase tracking-wide">
                  {typeLabels[type]}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {typeDescriptions[type]}
                </p>
              </div>
            </div>

            {/* Integrations List */}
            {typeIntegrations.length === 0 ? (
              <Card className="p-4">
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No hay integraciones configuradas
                </div>
              </Card>
            ) : (
              <div className="space-y-2">
                {typeIntegrations.map((integration) => (
                  <Card key={integration.id} className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Status Indicator */}
                      <div className={`w-2 h-2 rounded-full ${
                        integration.status === 'ENABLED' ? 'bg-emerald-500' :
                        integration.status === 'ERROR' ? 'bg-red-500' :
                        'bg-gray-400'
                      }`} />

                      {/* Integration Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-800 dark:text-white/90 truncate">
                            {integration.name}
                          </h4>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${statusColors[integration.status]}`}>
                            {statusIcons[integration.status]}
                            {integration.status}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                            Priority: {integration.priority}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                          <span>Provider: {integration.provider}</span>
                          <span>|</span>
                          <span>Last used: {formatDate(integration.lastUsedAt)}</span>
                          {integration.lastError && (
                            <>
                              <span>|</span>
                              <span className="text-red-500">{integration.lastError}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => onTest(integration.id)}
                          disabled={isTesting}
                          className="p-2 text-gray-400 hover:text-brand-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-50"
                          title="Probar conexión"
                        >
                          {isTesting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Zap className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => onEdit(integration)}
                          className="p-2 text-gray-400 hover:text-brand-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(integration)}
                          className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg ${
                            integration.status === 'ENABLED'
                              ? 'text-amber-500 hover:text-amber-600'
                              : 'text-emerald-500 hover:text-emerald-600'
                          }`}
                          title={integration.status === 'ENABLED' ? 'Deshabilitar' : 'Habilitar'}
                        >
                          {integration.status === 'ENABLED' ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(integration)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
